/**
 * AddonRequestService: add-on changes as a request to Scan Ninja (#945).
 *
 * `company.addons` and `company.featureFlags` are server-owned
 * (firestore.rules), so the plans pages no longer write them. Instead they
 * send the change to `POST /api/company/addon-requests`, which records it for
 * staff and the audit log; staff turn the add-on on from the admin console.
 * The bearer token and Functions key are added by the auth interceptors.
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { normalizeAddonIdsToSku } from '@core/data/addon-id-map';

/** What the customer asked to change. */
export interface AddonChangeRequest {
  /** Add-ons to add (catalog ids or entitlement SKUs). */
  requested?: string[];
  /** Add-ons to remove. */
  removed?: string[];
  /** Coming-soon add-ons the customer is interested in. */
  interest?: string[];
}

/** The API's answer, reduced to what the page shows. */
export interface AddonChangeResult {
  success: boolean;
  requestId?: string;
  /** A sentence the page can show as is. */
  message: string;
}

/** Fallback copy when the API's answer has no message. */
export const ADDON_REQUEST_SENT_MESSAGE =
  "Thanks. We've sent your add-on request to our team. They'll confirm the price and turn it on for your company.";

/** Copy for a failed request. */
export const ADDON_REQUEST_FAILED_MESSAGE =
  "We couldn't send your add-on request. Please try again or contact support.";

@Injectable({ providedIn: 'root' })
export class AddonRequestService {
  private http = inject(HttpClient);

  /**
   * Works out which add-ons were added and removed between two lists.
   *
   * @param current The company's add-ons now.
   * @param desired The add-ons the customer selected.
   * @returns The ids to request and to remove, as entitlement SKUs.
   */
  static diff(current: readonly string[], desired: readonly string[]): { requested: string[]; removed: string[] } {
    const have = new Set(normalizeAddonIdsToSku([...current]));
    const want = new Set(normalizeAddonIdsToSku([...desired]));
    return {
      requested: [...want].filter((id) => !have.has(id)),
      removed: [...have].filter((id) => !want.has(id)),
    };
  }

  /**
   * Sends an add-on change request. Never errors: a failure becomes
   * `{ success: false, message }`.
   *
   * @param change The add-ons to add, remove or register interest in.
   * @returns The outcome, with a message for the page.
   */
  request(change: AddonChangeRequest): Observable<AddonChangeResult> {
    return this.http
      .post<{ success?: boolean; requestId?: string; message?: string }>(
        `${environment.app_api_url}/api/company/addon-requests`,
        {
          requested: change.requested ?? [],
          removed: change.removed ?? [],
          interest: change.interest ?? [],
        },
      )
      .pipe(
        map((res) =>
          res?.success
            ? { success: true, requestId: res.requestId, message: res.message || ADDON_REQUEST_SENT_MESSAGE }
            : { success: false, message: ADDON_REQUEST_FAILED_MESSAGE },
        ),
        catchError((_error: HttpErrorResponse) => of({ success: false, message: ADDON_REQUEST_FAILED_MESSAGE })),
      );
  }
}
